import { Component, OnInit } from "@angular/core";
import { IVTableCell } from "@app/directives/_layout/v-table/_models/IVTableCell";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { FallbackCellType, VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { VehicleOnlineStatusComponent } from "@app/directives/_misc/vehicle-online-status-indicator/vehicle-online-status.component";
import { IFilterBarTab } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { FilterBarService } from "@app/directives/_resourceFilters/filter-bar/filter-bar.service";
import { SortDirection } from "@app/enum/SortDirection";
import { SortType } from "@app/enum/SortType";
import { VTableSelectionType } from "@app/enum/VTableSelectionType";
import { IConfigurableCameraDevice, IPeripheral } from "@app/interfaces/IPeripheral";
import { IAccount } from "@app/services/account.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import copy from "fast-copy";
import { combineLatest, forkJoin, Observable } from "rxjs";
import { tap } from "rxjs/operators";
import { AccountService } from "../../../_services/account.service";
import { PeripheralService } from "../../../_services/peripheral.service";
import { CameraApplicationState, ICameraApp } from "../application/_models/ICameraApp";
import { CameraAppService } from "../application/_services/camera-app.service";
import { ICameraConfiguration } from "../config/_models/ICameraConfiguration";
import { ConfigManagerService } from "../config/_services/config.service";
import './camera-configuration.component.scss';

@UntilDestroy()
@Component({
    selector: 'camera-configuration',
    template: require('./camera-configuration.component.html')
})
export class CameraConfigurationComponent implements OnInit {
    cameras: IConfigurableCameraDevice[];
    accounts: Map<number, IAccount>;
    accountsList: IAccount[];

    tableColumns: IVTableColumn<IConfigurableCameraDevice>[];
    tableRows: IVTableRow[];
    selectedRows: IVTableRow[];
    selectedDeviceColumns: IVTableColumn<IConfigurableCameraDevice>[];
    selectedDevices: IPeripheral[] = [];
    VTableSelectionType = VTableSelectionType;
    filterTabs: IFilterBarTab<IConfigurableCameraDevice>[];

    showDeviceConfigure: boolean;
    allConfigs: ICameraConfiguration[];
    allApps: ICameraApp[];
    availableConfigs: ICameraConfiguration[];
    availableApps: ICameraApp[];

    configToApply: ICameraConfiguration;
    appToApply: ICameraApp;

    forceUpdate: boolean;

    loading: boolean;

    constructor(
        private readonly accountService: AccountService,
        private readonly appService: CameraAppService,
        private readonly configService: ConfigManagerService,
        private readonly filterBarService: FilterBarService,
        private readonly peripheralService: PeripheralService,
        private readonly vTableService: VTableService,
        private readonly translationService: TranslationService
    ) { }

    ngOnInit(): void {
        this.loadData();
    }

    onSelectedRowsChanged(rows: IVTableRow[]): void {
        const selectedRowsCopy = copy(rows);
        this.selectedRows = selectedRowsCopy.map(row => {
            row.cells = row.cells.slice(1, 4);
            return row;
        });

        this.selectedDevices = rows.map(row => row.data);
    }

    selectConfig(config: ICameraConfiguration): void {
        this.configToApply = config;
    }

    resetSelectedActions(): void {
        delete this.configToApply;
        delete this.appToApply;

        this.forceUpdate = false;
    }

    openActionsSidebar(): void {
        this.selectedRows = this.selectedRows.map(row => {
            row.selected = false;
            return row;
        })
        this.showDeviceConfigure = true;
    }

    closeActionsSidebar(): void {
        this.resetSelectedActions();
        this.showDeviceConfigure = false;
    }

    filterCameraByAccount(camera: IPeripheral, account: IAccount): boolean {
        return camera.vehicleMapping?.vehicle?.AccountId === account.Id;
    }

    accountSearchPredicate(account: IAccount, searchText: string): boolean {
        return account.CompanyName.toLowerCase().indexOf(searchText.toLowerCase()) >= 0;
    }

    //--

    private loadData(): void {
        this.loading = true;
        this.createTabs();
        this.createTable();
        this.getAppsAndConfigs();

        combineLatest([
            this.loadAccounts(),
            this.loadCameras()
        ]).pipe(
            untilDestroyed(this)
        ).subscribe({
            next: () => {
                this.loading = false;
                this.tableRows = this.vTableService.createTableRows(this.cameras, this.tableColumns);
                this.createTabs();
            }
        });
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: "FoundationHardware.Installed", width: 150, minTableWidth: 400, sorted: true, sortDirection: SortDirection.Descending, sortType: SortType.Date }),
                createCellFunction: (camera) => this.vTableService.createDateCell(camera.vehicleMapping?.StartUtc)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "FoundationHardware.Serial", width: 150
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.Serial)
            },
            {
                header: this.vTableService.createVehicleTableHeader(),
                createCellFunction: (camera) => this.vTableService.createVehicleRegCell(camera.vehicleMapping?.vehicle)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "FoundationHardware.Name", minTableWidth: 500 }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.name)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "CameraConfiguration.CurrentConfig" }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.currentConfig, { tooltip: true })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "AppManagement.UpdaterApp", width: 150, minTableWidth: 450 }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.currentUpdaterAppVersion)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "AppManagement.TelematicsApp", width: 150, minTableWidth: 450 }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.currentTelematicsAppVersion)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Account", width: 250, minTableWidth: 450 }),
                createCellFunction: (camera) => this.vTableService.createTableCell(this.getCustomerAccountName(camera))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Status", width: 250, minTableWidth: 450, sortType: SortType.Date }),
                createCellFunction: camera => this.createCameraOnlineStatusCell(camera)
            }
        ]

        this.selectedDeviceColumns = copy(this.tableColumns.slice(1, 4));
    }

    createCameraOnlineStatusCell(camera: IConfigurableCameraDevice): IVTableCell {
        if (!camera.vehicleMapping || !camera.onlineStatus) {
            return this.vTableService.createDateCell(undefined, { fallbackCellType: FallbackCellType.Never });
        }

        camera.vehicleMapping.vehicle.onlineStatus = this.peripheralService.getOnlineStatusForVehicle(camera);

        let sortData = camera.vehicleMapping.vehicle.onlineStatus.sortingTime;

        if (camera.onlineStatus.Online) {
            sortData = new Date();
        }
        if (!camera.onlineStatus.Online && !camera.onlineStatus.LastOnlineTime) {
            sortData = undefined;
        }

        return this.vTableService.createTableCell(camera, {
            sortData,
            component: VehicleOnlineStatusComponent,
            componentInputs: { vehicle: camera.vehicleMapping?.vehicle }
        })

    }

    private getCustomerAccountName(peripheral: IPeripheral): string {
        if (!this.accounts?.size ||
            !peripheral?.vehicleMapping?.vehicle ||
            !this.accounts?.has(peripheral.vehicleMapping.vehicle.AccountId)) {
            return;
        }

        return this.accounts.get(peripheral.vehicleMapping.vehicle.AccountId).CompanyName;
    }

    private loadAccounts(): Observable<IAccount[]> {
        return this.accountService.getCustomerAccounts().pipe(
            untilDestroyed(this),
            tap(accounts => {
                this.accounts = new Map(accounts.map(account => [account.Id, account]));
                this.accountsList = accounts;
            })
        )
    }

    private loadCameras(): Observable<IConfigurableCameraDevice[]> {
        return this.peripheralService.getAllConfigurableCameras().pipe(
            tap(cameras => {
                this.cameras = cameras;
                this.setConfigNameString();
            })
        );
    }

    private setConfigNameString(): void {
        if (!this.allConfigs?.length) {
            return;
        }

        this.cameras.forEach(c => {
            c.currentConfig = this.allConfigs.find(config => config.Id == c.currentConfig)?.Name ?? this.translationService.t("Common.Unknown");
        });
    }

    private getAppsAndConfigs(): void {
        combineLatest([
            this.configService.getConfigs(),
            this.appService.getApplicationFiles()
        ]).pipe(
            untilDestroyed(this),
            tap(([configs, apps]) => {
                this.allApps = apps;
                this.allConfigs = configs;
                this.availableApps = apps.filter(app => app.IsActive && app.State === CameraApplicationState.Production);
                this.availableConfigs = configs.filter(config => config.IsActive);
            })
        ).subscribe();
    }

    private createTabs(): void {
        this.filterTabs = [];

        this.filterTabs.push(this.filterBarService.createTabDefinition<IConfigurableCameraDevice>("Common.Installed", (device) => !!device.vehicleMapping?.StartUtc));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IConfigurableCameraDevice>("Common.NotInstalled", (device) => !device.vehicleMapping?.StartUtc));
    }
}
